/**
 * **Navigation** — the profession line, the header menu and the footer
 * (C11, FR-I15 item 4, Wave 12C — T-4.60). Beside *Pages* on the site home.
 *
 * Three cards and one **Save**:
 *
 * 1. **The line under your name.** The profession's own words by default
 *    (*Chartered Accountants*, …), which the firm may edit or hide. The
 *    default is shown, and *Use the default* puts it back — an edited line is
 *    the firm's; an unedited one follows the profession.
 * 2. **The header menu.** Automatic until the firm customises it (every page
 *    marked *in the menu*, in order); then pages, external links, one level of
 *    dropdown and a button at the right. Each is edited in a dialog, moved up
 *    or down, or removed; *Back to automatic* is a reset.
 * 3. **The footer.** Today's until customised; then up to four link columns
 *    and the blocks the firm switches on. The disclaimers, the copyright and
 *    the *Powered by FiveOX* credit are always drawn and the card says so.
 *
 * **The limits are shown before the server refuses** (`menuProblems`): a
 * counter on each list, the *Add* buttons disabled at the limit, and the list
 * of what would be refused above **Save**, which is disabled while it is not
 * empty. The server holds the same rules and is the authority.
 *
 * **C17 and C18 (Wave 18D, T-4.74, T-4.77):**
 *
 * - A header item may be a **section on a page** (ADR-0036 §7.2): pick a page,
 *   then one of the sections on it that has an address. Header only — the
 *   footer's validator refuses it, so the footer's dialog does not offer it.
 * - **Client portal links** (spec §10.10): one control in the header card and
 *   one in the footer card, each storing `portal` {login, register: {show,
 *   label ≤30}} beside the menu. *Login* is shown and *Register* hidden until
 *   the firm says otherwise. *Register* is disabled, with the reason, while
 *   client sign-up is off. A firm on the automatic header or today's footer
 *   can change its portal links without customising the menu — the menu is
 *   then stored as `{portal}` alone (`joinMenu`).
 *
 * **C23 (Wave 25D, T-4.114, ADR-0045 §4–§5):** a header item, a dropdown and
 * each of its links may carry an **icon the firm picks** (`IconPicker`, in
 * the item's dialog). It is sent only when picked — an item without one is
 * exactly the item it was before C23 — and never on the button at the right
 * or in the footer, which the service does not accept.
 *
 * **Everything here edits the draft** (FR-I15 item 5; the owner, 2026-09-25:
 * *"Go live on Publish"*): a save shows on the preview link at once and on the
 * live site when the firm publishes, exactly as its pages do. The screen says
 * so at the top, and says when the draft's navigation differs from what is
 * live (`differs_from_live`, which the service works out by page slug).
 */

import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  Field,
  Input,
  Select,
  Skeleton,
  Switch,
} from '@fiveox/ui'
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { SiteIcon } from '../../../site/icons'
import { LayoutDiagram, layoutWords } from '../../components/site/LayoutDiagram'
import { EditBar, EditPage, useDraftPreviewHref, useLeaveGuard } from '../../components/edit'
import { IconPicker } from '../../components/IconPicker'
import { SITE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  menuFromPages,
  menuProblems,
  menuUrlProblem,
  moveItem,
  PORTAL_LABEL_MAX,
  PORTAL_SETTING_DEFAULTS,
  site,
  type FooterArrangementItem,
  type FooterBlock,
  type FooterMenu,
  type HeaderMenu,
  type IconSet,
  type MenuItem,
  type MenuLink,
  type Navigation,
  type NavigationPage,
  type NavigationSave,
  type PortalOnly,
  type PortalSetting,
} from '../../lib/api/site'
import { signIn } from '../../lib/api/signIn'
import { useSite } from './useSite'

/** The footer blocks, in the firm's words. `client_login` is not drawn as a
 *  switch any more — the *Client portal links* control replaces it (C18) — and
 *  is kept here so an older reader of this map still has a name for it. */
export const FOOTER_BLOCK_LABELS: Record<FooterBlock, { label: string; note: string }> = {
  address: { label: 'Address', note: 'From Your firm' },
  hours: { label: 'Working hours', note: 'From your contact details' },
  contact: { label: 'Email and phone', note: 'From your contact details' },
  social: { label: 'Social links', note: 'Set in Social profiles & map' },
  map: { label: 'Map link', note: 'Set in Social profiles & map' },
  client_login: { label: 'Login', note: 'Your client portal link' },
}

/* ── C18: the portal entry beside a menu ─────────────────────────────────── */

/**
 * A stored menu, split into the menu the firm edits and its `portal`. A
 * stored `{portal}` alone is the automatic header (or today's footer) with the
 * firm's portal links — `menu` is `null` for it.
 */
export function splitMenu<M extends object>(
  stored: M | PortalOnly | null | undefined,
  ...keys: string[]
): { menu: M | null; portal: PortalSetting | null } {
  if (!stored || typeof stored !== 'object') return { menu: null, portal: null }
  const portal = ((stored as { portal?: PortalSetting }).portal ?? null) as PortalSetting | null
  if (!keys.some((key) => key in stored)) return { menu: null, portal }
  const { portal: _drop, ...menu } = stored as M & { portal?: PortalSetting }
  void _drop
  return { menu: menu as M, portal }
}

/** The inverse of `splitMenu`: what the save sends for one surface. */
export function joinMenu<M extends object>(menu: M | null, portal: PortalSetting | null): M | PortalOnly | null {
  if (menu && portal) return { ...menu, portal }
  if (menu) return menu
  return portal ? { portal } : null
}

/**
 * What a surface's portal control shows before the firm has changed it: the
 * defaults, except the footer's older `client_login` switch, which is read as
 * *Login*'s (spec §10.10 item 2).
 */
export function effectivePortal(portal: PortalSetting | null, clientLogin = true): PortalSetting {
  if (portal) return portal
  return {
    login: { ...PORTAL_SETTING_DEFAULTS.login, show: clientLogin },
    register: { ...PORTAL_SETTING_DEFAULTS.register },
  }
}

/** Why a portal setting would be refused, as sentences. */
export function portalProblems(portal: PortalSetting | null, where: string): string[] {
  if (!portal) return []
  const out: string[] = []
  for (const name of ['login', 'register'] as const) {
    if ((portal[name].label ?? '').length > PORTAL_LABEL_MAX) {
      out.push(`${where}: keep ${name === 'login' ? 'Login' : 'Register'}’s label to ${PORTAL_LABEL_MAX} characters.`)
    }
  }
  return out
}

/* ── C17: a section on a page ────────────────────────────────────────────── */

/** One section on a page that a menu can point at — it has an address. */
export interface SectionAnchor {
  anchor: string
  /** The section's own menu label, if the firm gave it one. */
  label: string | null
  type: string
}

/** The addressed sections of a page, in page order (ADR-0036 §7.1). */
export function pageAnchors(page: { blocks?: { type: string; props: Record<string, unknown> }[] } | null): SectionAnchor[] {
  const out: SectionAnchor[] = []
  for (const block of page?.blocks ?? []) {
    const anchor = typeof block.props?.anchor === 'string' ? block.props.anchor.trim() : ''
    if (!anchor) continue
    const label = typeof block.props?.anchor_label === 'string' ? block.props.anchor_label.trim() : ''
    out.push({ anchor, label: label || null, type: block.type })
  }
  return out
}

/** Reads a page's addressed sections — the route's, replaced in tests. */
export type LoadAnchors = (pageId: string) => Promise<SectionAnchor[]>

const loadAnchorsFromSite: LoadAnchors = async (pageId) => pageAnchors((await site.page(pageId)).data)

/** Where the social links and the map link are typed in (2026-09-25). Until
 *  then no staff screen edited them, so these two switches drew something a
 *  firm could not fill in. Its own page under the website since Wave 14
 *  (C13, ADR-0032 §1); `/settings/firm#social` redirects here. */
export const SOCIAL_SETTINGS_HREF = '/site/social'

/** Whether the firm has anything for the two footer blocks it cannot fill in
 *  here — read from the site, `null` when unknown. */
export interface FooterFacts {
  social: boolean
  map: boolean
}

export function footerFacts(site: { social?: Record<string, string> | null; contact?: Record<string, string> | null } | null): FooterFacts | null {
  if (!site) return null
  return {
    social: Object.values(site.social ?? {}).some((url) => Boolean(url)),
    map: Boolean(site.contact?.map_url),
  }
}

/** What a page link reads as in the builder — its own label, else the page's. */
export function linkName(link: MenuLink, pages: NavigationPage[]): string {
  if (link.kind === 'link') return link.label || link.url
  if (link.kind === 'section') return link.label || `#${link.anchor}`
  const page = pages.find((p) => p.id === link.page_id)
  return link.label || page?.nav_label || page?.title || 'A page that no longer exists'
}

function linkDetail(link: MenuLink, pages: NavigationPage[]): string {
  if (link.kind === 'link') return `${link.url}${link.new_tab ? ' · opens in a new tab' : ''}`
  const page = pages.find((p) => p.id === link.page_id)
  if (link.kind === 'section') return page ? `${page.href}#${link.anchor} · a section` : 'Removed — it will not be shown'
  return page ? page.href : 'Removed — it will not be shown'
}

/* ── the link dialog ─────────────────────────────────────────────────────── */

type Editing =
  | {
      kind: 'link'
      title: string
      value: MenuLink
      labelRequired: boolean
      /** C17: a header item may point at a section; a footer link may not. */
      allowSection: boolean
      /** C23: a header item or a dropdown's link may carry an icon; the
       *  button and the footer's links may not (ADR-0045 §4). */
      allowIcon: boolean
      save: (link: MenuLink) => void
    }
  | {
      kind: 'dropdown'
      title: string
      value: string
      icon: string | null
      save: (label: string, icon: string | null) => void
    }

/** C23: `item` with the picked icon, or without the key at all — never
 *  `icon: null` — so a menu saved without icons is the menu it was. */
function withIcon<T extends object>(item: T, icon: string | null | undefined): T {
  const rest = { ...item } as T & { icon?: unknown }
  delete rest.icon
  return (icon ? { ...rest, icon } : rest) as T
}

/**
 * **One line** — C24 item 12 (*"below h1 too much text required it in 1
 * line"*). The fifty-four words it replaced said three things: what is on this
 * screen, that a template styles it, and that nothing is live until a publish.
 * The third is said by the edit bar and by the draft notice below, and the
 * second is said by the layout diagrams this screen now draws (C24 item 13).
 */
/**
 * **The footer a firm arranges** — C24 item 14, ADR-0046 §3, T-4.121.
 *
 * The owner, 2026-10-01: *"at footer we should able to set position, size of
 * website name, address, email, mobile, hrs, links, etc"*.
 *
 * Ten rows, one per element. Each carries which column it sits in, how loud it
 * is, and up/down buttons — **the list's order is the order**, so moving a row
 * is the whole of saying "this comes first" (ADR-0046 §3 explains why there is
 * no stored number beside it).
 *
 * **Not a canvas.** There is no drag-and-drop and no pixel position: both were
 * offered to the owner and neither was chosen, and each would need a live
 * preview surface this screen does not have.
 */
/**
 * **The header and footer this firm has chosen, drawn** — C24 item 13.
 *
 * The owner asked for the same section in both places. It is read-only here:
 * *How your site looks* is where a layout is chosen, and two screens that both
 * wrote it would be two answers to one question. So this shows and links.
 */
export function ChosenLayouts({ header, footer }: { header: string; footer: string }) {
  return (
    <div className="space-y-2" data-chosen-layouts="">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-foreground">Your header and footer</p>
        <Link to="/site/theme#layout" className="text-xs font-medium text-primary underline">
          Change them in How your site looks
        </Link>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {([
          ['header', header],
          ['footer', footer],
        ] as const).map(([surface, layout]) => (
          <div key={surface} className="rounded-lg border border-border p-3">
            <LayoutDiagram layout={layout} surface={surface} />
            <p className="mt-2 text-sm font-medium text-foreground">
              {layoutWords(surface, layout).label}
            </p>
            <p className="text-xs text-muted-foreground">{layoutWords(surface, layout).note}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

const ARRANGEMENT_LABELS: Record<string, string> = {
  name: 'Your firm’s name',
  line: 'The line under your name',
  address: 'Your address',
  email: 'Your email',
  phone: 'Your phone number',
  hours: 'Your working hours',
  map: 'The map link',
  social: 'Your social links',
  client_login: 'The client portal links',
  columns: 'Your link columns',
}

const SIZE_LABELS: Record<string, string> = {
  small: 'Small',
  normal: 'Normal',
  large: 'Large',
}

/** The arrangement a firm has not touched: everything in its usual place. */
export function defaultArrangement(keys: readonly string[]): FooterArrangementItem[] {
  return keys.map((key) => ({ key: key as FooterArrangementItem['key'], slot: 1, size: 'normal' }))
}

/** Move one row, by index. Out of range is a no-op, so the ends are safe. */
export function moveArrangement(
  items: FooterArrangementItem[],
  from: number,
  to: number,
): FooterArrangementItem[] {
  if (to < 0 || to >= items.length || from === to) return items
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved!)
  return next
}

export function FooterArrangement({
  value,
  keys,
  sizes,
  maxSlot,
  editable,
  onChange,
}: {
  value: FooterArrangementItem[] | null
  keys: readonly string[]
  sizes: readonly string[]
  maxSlot: number
  editable: boolean
  onChange: (next: FooterArrangementItem[] | null) => void
}) {
  const items = value ?? defaultArrangement(keys)
  const set = (index: number, patch: Partial<FooterArrangementItem>) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)))

  return (
    <fieldset className="space-y-2" data-footer-arrangement="">
      <legend className="text-sm font-medium text-foreground">Where each thing sits</legend>
      <p className="text-xs text-muted-foreground">
        Put each thing in a column and choose how big it is. The order here is the order it is drawn
        in. Some layouts draw fewer columns — anything in a column they do not have moves into the
        last one, so nothing is ever lost.
      </p>
      <ol className="grid gap-2">
        {items.map((item, i) => (
          <li
            key={item.key}
            data-arrangement-row={item.key}
            className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-2.5"
          >
            <span className="min-w-[10rem] flex-1 text-sm text-foreground">
              {ARRANGEMENT_LABELS[item.key] ?? item.key}
            </span>
            <Select
              aria-label={`Column for ${ARRANGEMENT_LABELS[item.key] ?? item.key}`}
              value={String(item.slot)}
              size="sm"
              disabled={!editable}
              onChange={(e) => set(i, { slot: Number(e.target.value) })}
            >
              <option value="0">Not shown</option>
              {Array.from({ length: maxSlot }, (_, n) => (
                <option key={n} value={String(n + 1)}>{`Column ${n + 1}`}</option>
              ))}
            </Select>
            <Select
              aria-label={`Size of ${ARRANGEMENT_LABELS[item.key] ?? item.key}`}
              value={item.size}
              size="sm"
              disabled={!editable}
              onChange={(e) => set(i, { size: e.target.value as FooterArrangementItem['size'] })}
            >
              {sizes.map((size) => (
                <option key={size} value={size}>
                  {SIZE_LABELS[size] ?? size}
                </option>
              ))}
            </Select>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Move ${ARRANGEMENT_LABELS[item.key] ?? item.key} up`}
              disabled={!editable || i === 0}
              onClick={() => onChange(moveArrangement(items, i, i - 1))}
            >
              <ArrowUp size={14} aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Move ${ARRANGEMENT_LABELS[item.key] ?? item.key} down`}
              disabled={!editable || i === items.length - 1}
              onClick={() => onChange(moveArrangement(items, i, i + 1))}
            >
              <ArrowDown size={14} aria-hidden />
            </Button>
          </li>
        ))}
      </ol>
      {value ? (
        <Button variant="ghost" size="sm" disabled={!editable} onClick={() => onChange(null)}>
          Put everything back where it was
        </Button>
      ) : null}
    </fieldset>
  )
}

const NAVIGATION_DESCRIPTION =
  'Your profession line, the menu at the top of every page and the footer at the bottom.'

const KIND_LABELS: Record<MenuLink['kind'], string> = {
  page: 'A page of your site',
  section: 'Section on a page',
  link: 'An address elsewhere',
}

function LinkDialog({
  editing,
  pages,
  limits,
  loadAnchors,
  icons,
  onClose,
}: {
  editing: Extract<Editing, { kind: 'link' }>
  pages: NavigationPage[]
  limits: Navigation['limits']
  loadAnchors: LoadAnchors
  icons: IconSet | null
  onClose: () => void
}) {
  const [icon, setIcon] = useState<string | null>(editing.value.icon ?? null)
  const [kind, setKind] = useState<MenuLink['kind']>(editing.value.kind)
  const [pageId, setPageId] = useState(editing.value.kind !== 'link' ? editing.value.page_id : (pages[0]?.id ?? ''))
  const [label, setLabel] = useState(editing.value.label ?? '')
  const [url, setUrl] = useState(editing.value.kind === 'link' ? editing.value.url : 'https://')
  const [newTab, setNewTab] = useState(editing.value.kind === 'link' ? Boolean(editing.value.new_tab) : false)
  const [anchor, setAnchor] = useState(editing.value.kind === 'section' ? editing.value.anchor : '')
  const [anchors, setAnchors] = useState<SectionAnchor[] | null>(null)
  const [tried, setTried] = useState(false)

  // C17: the addressed sections of the chosen page, read when a section is
  // being chosen. A read that fails leaves the list empty, which says so.
  useEffect(() => {
    if (kind !== 'section' || !pageId) return
    let live = true
    setAnchors(null)
    loadAnchors(pageId).then(
      (found) => live && setAnchors(found),
      () => live && setAnchors([]),
    )
    return () => {
      live = false
    }
  }, [kind, pageId, loadAnchors])

  const kinds: MenuLink['kind'][] = editing.allowSection ? ['page', 'section', 'link'] : ['page', 'link']
  const max = editing.labelRequired && editing.title.includes('button') ? limits.cta_label : limits.label
  const labelProblem =
    (kind === 'link' || kind === 'section' || editing.labelRequired) && !label.trim()
      ? 'Give it a label.'
      : label.length > max
        ? `Keep it under ${max} characters.`
        : null
  const urlProblem = kind === 'link' ? menuUrlProblem(url) : null
  const pageProblem = kind !== 'link' && !pages.some((p) => p.id === pageId) ? 'Choose a page.' : null
  const anchorProblem = kind === 'section' && !anchor ? 'Choose a section on the page.' : null
  const invalid = Boolean(labelProblem || urlProblem || pageProblem || anchorProblem)
  const page = pages.find((p) => p.id === pageId)
  // An anchor stored earlier and since renamed on the page is still offered,
  // marked, so opening the dialog never silently changes the link.
  const anchorOptions =
    anchors && anchor && !anchors.some((a) => a.anchor === anchor)
      ? [...anchors, { anchor, label: null, type: 'missing' }]
      : (anchors ?? [])

  const submit = () => {
    setTried(true)
    if (invalid) return
    const link: MenuLink =
      kind === 'page'
        ? { kind: 'page', page_id: pageId, label: label.trim() || null }
        : kind === 'section'
          ? { kind: 'section', page_id: pageId, anchor, label: label.trim() }
          : { kind: 'link', label: label.trim(), url: url.trim(), new_tab: newTab }
    editing.save(editing.allowIcon ? withIcon(link, icon) : link)
    onClose()
  }

  return (
    <Dialog open onClose={onClose} title={editing.title}>
      <div className="space-y-4">
        <div role="radiogroup" aria-label="What it links to" className="flex flex-wrap gap-1 rounded-lg border border-border p-1">
          {kinds.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={kind === value}
              data-link-kind={value}
              onClick={() => setKind(value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                kind === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {KIND_LABELS[value]}
            </button>
          ))}
        </div>
        {kind !== 'link' ? (
          <>
            <Select
              label="Page"
              value={pageId}
              onChange={(e) => {
                setPageId(e.target.value)
                setAnchor('')
              }}
              error={tried ? pageProblem : null}
            >
              {pages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} — {p.href}
                </option>
              ))}
            </Select>
            {kind === 'section' &&
              (anchors === null ? (
                <p className="text-sm text-muted-foreground">Reading the sections on this page…</p>
              ) : anchorOptions.length === 0 ? (
                <Alert tone="info">
                  No section on this page has an address yet. Give one a <em>Section address</em> in
                  the page editor, then come back.
                </Alert>
              ) : (
                <Select
                  label="Section"
                  value={anchor}
                  error={tried ? anchorProblem : null}
                  required
                  aria-required="true"
                  onChange={(e) => {
                    const next = e.target.value
                    setAnchor(next)
                    const own = anchors?.find((a) => a.anchor === next)?.label
                    if (own && !label.trim()) setLabel(own)
                  }}
                >
                  {/* C27 (UAT report #13): a placeholder, not a choice. */}
                  <option value="" disabled hidden>
                    Choose a section…
                  </option>
                  {anchorOptions.map((a) => (
                    <option key={a.anchor} value={a.anchor}>
                      {a.label ? `${a.label} — #${a.anchor}` : `#${a.anchor}`}
                      {a.type === 'missing' ? ' (no longer on the page)' : ''}
                    </option>
                  ))}
                </Select>
              ))}
          </>
        ) : (
          <Field
            label="Address"
            hint="https://, http://, mailto: or tel: — for one of your own pages, choose a page instead."
            error={tried ? urlProblem : null}
          >
            {(control) => <Input {...control} value={url} onChange={(e) => setUrl(e.target.value)} />}
          </Field>
        )}
        <Field
          label="Label"
          hint={
            kind === 'page' && !editing.labelRequired
              ? `Leave it empty to use the page’s own name${page ? ` — “${page.nav_label || page.title}”` : ''}.`
              : undefined
          }
          error={tried ? labelProblem : null}
        >
          {(control) => (
            <Input
              {...control}
              value={label}
              maxLength={max + 20}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={kind === 'page' ? (page?.nav_label || page?.title) : 'Our fees'}
            />
          )}
        </Field>
        {kind === 'link' && (
          <Switch checked={newTab} onChange={(e) => setNewTab(e.target.checked)} label="Open in a new tab" />
        )}
        {editing.allowIcon && icons && (
          <Field label="Icon" hint="Optional. Drawn beside the label in your header.">
            {(control) => (
              <IconPicker
                id={control.id}
                aria-describedby={control['aria-describedby']}
                value={icon}
                onChange={setIcon}
                icons={icons.icons}
                groups={icons.groups}
              />
            )}
          </Field>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit}>Done</Button>
        </div>
      </div>
    </Dialog>
  )
}

function DropdownDialog({
  editing,
  max,
  icons,
  onClose,
}: {
  editing: Extract<Editing, { kind: 'dropdown' }>
  max: number
  icons: IconSet | null
  onClose: () => void
}) {
  const [label, setLabel] = useState(editing.value)
  const [icon, setIcon] = useState<string | null>(editing.icon)
  const [tried, setTried] = useState(false)
  const problem = !label.trim() ? 'Give the dropdown a label.' : label.length > max ? `Keep it under ${max} characters.` : null
  return (
    <Dialog open onClose={onClose} title={editing.title}>
      <div className="space-y-4">
        <Field label="Label" hint="What visitors click to open it — “Services”, “Resources”." error={tried ? problem : null}>
          {(control) => <Input {...control} value={label} onChange={(e) => setLabel(e.target.value)} />}
        </Field>
        {icons && (
          <Field label="Icon" hint="Optional. Drawn beside the dropdown’s label in your header.">
            {(control) => (
              <IconPicker
                id={control.id}
                aria-describedby={control['aria-describedby']}
                value={icon}
                onChange={setIcon}
                icons={icons.icons}
                groups={icons.groups}
              />
            )}
          </Field>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setTried(true)
              if (problem) return
              editing.save(label.trim(), icon)
              onClose()
            }}
          >
            Done
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/* ── one row ─────────────────────────────────────────────────────────────── */

function Row({
  name,
  icon,
  detail,
  badge,
  editable,
  first,
  last,
  onUp,
  onDown,
  onEdit,
  onRemove,
  children,
}: {
  name: string
  /** C23: the picked icon, drawn before the name as the site will draw it. */
  icon?: string | null
  detail?: string
  badge?: string
  editable: boolean
  first: boolean
  last: boolean
  onUp: () => void
  onDown: () => void
  onEdit: () => void
  onRemove: () => void
  children?: React.ReactNode
}) {
  return (
    <li className="rounded-xl border border-border bg-surface-raised p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {icon && (
              <span className="mr-1.5 inline-flex align-[-2px] text-muted-foreground" data-row-icon={icon}>
                <SiteIcon name={icon} size={14} />
              </span>
            )}
            {name} {badge && <Badge size="sm">{badge}</Badge>}
          </p>
          {detail && <p className="truncate text-xs text-muted-foreground">{detail}</p>}
        </div>
        {editable && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" aria-label={`Move ${name} up`} disabled={first} onClick={onUp}>
              <ArrowUp size={14} />
            </Button>
            <Button variant="ghost" size="sm" aria-label={`Move ${name} down`} disabled={last} onClick={onDown}>
              <ArrowDown size={14} />
            </Button>
            <Button variant="ghost" size="sm" aria-label={`Edit ${name}`} onClick={onEdit}>
              <Pencil size={14} />
            </Button>
            <Button variant="ghost" size="sm" aria-label={`Remove ${name}`} onClick={onRemove}>
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </div>
      {children}
    </li>
  )
}

function Counter({ used, max, what }: { used: number; max: number; what: string }) {
  return (
    <span className={`text-xs ${used >= max ? 'font-medium text-amber-800' : 'text-muted-foreground'}`} data-counter={what}>
      {used} of {max} {what}
    </span>
  )
}

/**
 * **Client portal links** — C18, spec §10.10. One per surface. *Register* is
 * disabled, and says why, while client sign-up is off: the site would not
 * draw it whatever the switch said (I-6).
 */
function PortalControl({
  surface,
  value,
  editable,
  signupAllowed,
  onChange,
}: {
  surface: 'header' | 'footer'
  value: PortalSetting
  editable: boolean
  /** `false` when the firm has turned client sign-up off; `null` unknown. */
  signupAllowed: boolean | null
  onChange: (next: PortalSetting) => void
}) {
  const where = surface === 'header' ? 'header' : 'footer'
  const set = (name: 'login' | 'register', patch: Partial<PortalSetting['login']>) =>
    onChange({ ...value, [name]: { ...value[name], ...patch } })
  const labelField = (name: 'login' | 'register', fallback: string) => {
    const label = value[name].label ?? ''
    return (
      <Field
        label={`${fallback}’s label`}
        hint={`Up to ${PORTAL_LABEL_MAX} characters. Leave it empty for “${fallback}”.`}
        error={label.length > PORTAL_LABEL_MAX ? `Keep it to ${PORTAL_LABEL_MAX} characters.` : null}
      >
        {(control) => (
          <Input
            {...control}
            value={label}
            disabled={!editable}
            placeholder={fallback}
            data-portal-label={`${surface}-${name}`}
            onChange={(e) => set(name, { label: e.target.value || null })}
          />
        )}
      </Field>
    )
  }
  return (
    <fieldset className="space-y-3 rounded-xl border border-border p-3" data-portal-control={surface}>
      <legend className="px-1 text-sm font-medium text-foreground">Client portal links</legend>
      <p className="text-xs text-muted-foreground">
        The links in your {where} that take clients to your client portal, on your own address.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="min-w-0 space-y-2">
          <Switch
            checked={value.login.show}
            disabled={!editable}
            onChange={(e) => set('login', { show: e.target.checked })}
            label="Show Login"
          />
          {labelField('login', 'Login')}
        </div>
        <div className="min-w-0 space-y-2">
          <Switch
            checked={value.register.show}
            disabled={!editable || signupAllowed === false}
            onChange={(e) => set('register', { show: e.target.checked })}
            label="Show Register"
          />
          {signupAllowed === false && (
            <p className="text-xs text-muted-foreground" data-register-reason={surface}>
              Client sign-up is off, so Register cannot show — clients join when you invite them.
              Turn sign-up on in{' '}
              <Link to="/site/client-sign-in" className="font-medium text-primary underline">
                Client sign-in
              </Link>
              .
            </p>
          )}
          {labelField('register', 'Register')}
        </div>
      </div>
    </fieldset>
  )
}

/* ── the editor ──────────────────────────────────────────────────────────── */

const blankPage = (pages: NavigationPage[]): MenuLink => ({ kind: 'page', page_id: pages[0]?.id ?? '', label: null })
const blankLink = (): MenuLink => ({ kind: 'link', label: '', url: 'https://', new_tab: false })
const blankSection = (pages: NavigationPage[]): MenuLink => ({
  kind: 'section',
  // The home page first: a one-page site's sections live there.
  page_id: (pages.find((p) => p.slug === 'home') ?? pages[0])?.id ?? '',
  anchor: '',
  label: '',
})

/**
 * The editor itself, with no fetching of its own — `SiteNavigation` below
 * reads and saves; a test mounts this with a `Navigation` and an `onSave`.
 */
export function NavigationEditor({
  initial,
  editable,
  onSave,
  previewHref,
  facts = null,
  signupAllowed = null,
  loadAnchors = loadAnchorsFromSite,
  icons = null,
}: {
  initial: Navigation
  editable: boolean
  onSave: (body: NavigationSave) => Promise<Navigation>
  previewHref: string | null
  /** Whether the social links and map link have anything in them. */
  facts?: FooterFacts | null
  /** C18: whether client sign-up is allowed — `null` when unknown. */
  signupAllowed?: boolean | null
  /** C17: how a page's addressed sections are read. */
  loadAnchors?: LoadAnchors
  /** C23: the vocabulary's icon set — no picker is drawn without it. */
  icons?: IconSet | null
}) {
  const splitHeader = (nav: Navigation) => splitMenu<HeaderMenu>(nav.header_menu, 'items', 'cta')
  const splitFooter = (nav: Navigation) => splitMenu<FooterMenu>(nav.footer_menu, 'columns', 'blocks')
  const [saved, setSaved] = useState(initial)
  const [line, setLine] = useState(initial.profession_line ?? '')
  const [showLine, setShowLine] = useState(initial.show_profession_line)
  const [header, setHeader] = useState<HeaderMenu | null>(() => splitHeader(initial).menu)
  const [headerPortal, setHeaderPortal] = useState<PortalSetting | null>(() => splitHeader(initial).portal)
  const [footer, setFooter] = useState<FooterMenu | null>(() => splitFooter(initial).menu)
  const [footerPortal, setFooterPortal] = useState<PortalSetting | null>(() => splitFooter(initial).portal)
  const [editing, setEditing] = useState<Editing | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const { pages, limits } = saved
  const problems = useMemo(
    () => [
      ...menuProblems(header, footer, limits),
      ...portalProblems(headerPortal, 'Header'),
      ...portalProblems(footerPortal, 'Footer'),
    ],
    [header, footer, limits, headerPortal, footerPortal],
  )
  const lineProblem = line.length > limits.profession_line ? `Keep the line under ${limits.profession_line} characters.` : null

  const body: NavigationSave = {
    profession_line: line.trim() || null,
    show_profession_line: showLine,
    header_menu: joinMenu(header, headerPortal),
    footer_menu: joinMenu(footer, footerPortal),
  }
  const savedHeader = splitHeader(saved)
  const savedFooter = splitFooter(saved)
  const dirty =
    JSON.stringify(body) !==
    JSON.stringify({
      profession_line: saved.profession_line,
      show_profession_line: saved.show_profession_line,
      header_menu: joinMenu(savedHeader.menu, savedHeader.portal),
      footer_menu: joinMenu(savedFooter.menu, savedFooter.portal),
    })

  // Leaving with unsaved changes asks first — a route change and a tab close
  // (C13, ADR-0032 §5; the one guard in `components/edit`).
  const guard = useLeaveGuard(dirty)

  const load = (nav: Navigation) => {
    const h = splitHeader(nav)
    const f = splitFooter(nav)
    setLine(nav.profession_line ?? '')
    setShowLine(nav.show_profession_line)
    setHeader(h.menu)
    setHeaderPortal(h.portal)
    setFooter(f.menu)
    setFooterPortal(f.portal)
  }

  const discard = () => {
    load(saved)
    setNotice(null)
    setError(null)
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const next = await onSave(body)
      setSaved(next)
      load(next)
      setNotice('Saved to your draft. Your preview shows it now; your live site shows it when you publish.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Your navigation could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  /* header helpers */
  const items = header?.items ?? []
  const setItems = (next: MenuItem[]) => setHeader({ items: next, cta: header?.cta ?? null })
  const replaceItem = (i: number, item: MenuItem) => setItems(items.map((x, j) => (j === i ? item : x)))
  const atTop = items.length >= limits.top_items

  // A header item (top-level, a dropdown's link or the button) may point at a
  // section; a footer link may not — `editFooterLink` below.
  // C23: every header link takes an icon except the button (`cta`), which is
  // the one edited with a required label.
  const editLink = (title: string, value: MenuLink, save: (link: MenuLink) => void, labelRequired = false) =>
    setEditing({ kind: 'link', title, value, save, labelRequired, allowSection: true, allowIcon: !labelRequired })
  const editFooterLink = (title: string, value: MenuLink, save: (link: MenuLink) => void) =>
    setEditing({ kind: 'link', title, value, save, labelRequired: false, allowSection: false, allowIcon: false })

  /* footer helpers */
  const columns = footer?.columns ?? []
  const setColumns = (next: FooterMenu['columns']) =>
    setFooter({ columns: next, blocks: footer?.blocks ?? { ...saved.footer_block_defaults } })

  return (
    <EditPage
      title="Navigation"
      back={{ to: '/site', label: 'Your website' }}
      description={NAVIGATION_DESCRIPTION}
    >
      {guard}

      {saved.differs_from_live && !dirty && (
        <Alert tone="info">
          Your draft’s navigation is not live yet. Check it in your preview, then publish from{' '}
          <Link to="/site" className="font-medium underline">
            Your website
          </Link>
          .
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {/* ── 1. the profession line ──────────────────────────────────────── */}
      <Card>
        <CardBody className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">The line under your name</h2>
            <p className="text-sm text-muted-foreground">
              Shown under your firm’s name in the header and in the footer.
              {saved.profession_default
                ? ` Unless you write your own, it reads “${saved.profession_default}” — and follows your profession if that changes.`
                : ' Choose your profession in Settings and a line appears here by itself.'}
            </p>
          </div>
          <Switch
            checked={showLine}
            disabled={!editable}
            onChange={(e) => setShowLine(e.target.checked)}
            label="Show the line under my name"
          />
          {showLine && (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Your own words" className="min-w-0 flex-1" error={lineProblem} hint="Leave it empty to use your profession’s line.">
                {(control) => (
                  <Input
                    {...control}
                    value={line}
                    disabled={!editable}
                    placeholder={saved.profession_default ?? 'Chartered Accountants & Tax Advisers'}
                    onChange={(e) => setLine(e.target.value)}
                  />
                )}
              </Field>
              {editable && line && (
                <Button variant="outline" onClick={() => setLine('')}>
                  Use the default
                </Button>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── 2. the header menu ──────────────────────────────────────────── */}
      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold text-foreground">Header menu</h2>
              <p className="text-sm text-muted-foreground">
                {header
                  ? 'Your own menu. Pages you delete drop out of it by themselves.'
                  : 'Automatic: every page marked “in the menu”, in the order of your pages.'}
              </p>
            </div>
            {header && <Counter used={items.length} max={limits.top_items} what="items" />}
          </div>

          {!header ? (
            <div className="rounded-xl border border-dashed border-border p-4">
              <p className="text-sm text-muted-foreground">
                Showing now:{' '}
                {pages.filter((p) => p.in_nav).map((p) => p.nav_label || p.title).join(' · ') || 'no pages yet'}
              </p>
              {editable && (
                <Button className="mt-3" variant="outline" onClick={() => setHeader(menuFromPages(pages))} disabled={pages.length === 0}>
                  Customise the menu
                </Button>
              )}
            </div>
          ) : (
            <>
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground">No items yet — add a page, a link or a dropdown.</p>
              ) : (
                <ol className="space-y-2" aria-label="Header menu items">
                  {items.map((item, i) => {
                    const common = {
                      editable,
                      first: i === 0,
                      last: i === items.length - 1,
                      onUp: () => setItems(moveItem(items, i, i - 1)),
                      onDown: () => setItems(moveItem(items, i, i + 1)),
                      onRemove: () => setItems(items.filter((_, j) => j !== i)),
                    }
                    if (item.kind === 'dropdown') {
                      const full = item.items.length >= limits.dropdown_items
                      return (
                        <Row
                          key={i}
                          {...common}
                          name={item.label || 'Dropdown'}
                          icon={item.icon}
                          badge="Dropdown"
                          detail={`${item.items.length} of ${limits.dropdown_items} links`}
                          onEdit={() =>
                            setEditing({
                              kind: 'dropdown',
                              title: 'Edit the dropdown',
                              value: item.label,
                              icon: item.icon ?? null,
                              save: (label, icon) => replaceItem(i, withIcon({ ...item, label }, icon)),
                            })
                          }
                        >
                          <ol className="mt-2 space-y-2 border-l border-border pl-3">
                            {item.items.map((child, j) => (
                              <Row
                                key={j}
                                editable={editable}
                                name={linkName(child, pages)}
                                icon={child.icon}
                                detail={linkDetail(child, pages)}
                                first={j === 0}
                                last={j === item.items.length - 1}
                                onUp={() => replaceItem(i, { ...item, items: moveItem(item.items, j, j - 1) })}
                                onDown={() => replaceItem(i, { ...item, items: moveItem(item.items, j, j + 1) })}
                                onRemove={() => replaceItem(i, { ...item, items: item.items.filter((_, k) => k !== j) })}
                                onEdit={() =>
                                  editLink('Edit the link', child, (link) =>
                                    replaceItem(i, { ...item, items: item.items.map((c, k) => (k === j ? link : c)) }),
                                  )
                                }
                              />
                            ))}
                          </ol>
                          {editable && (
                            <Button
                              className="mt-2"
                              variant="ghost"
                              size="sm"
                              disabled={full}
                              onClick={() =>
                                editLink(`Add a link to “${item.label}”`, blankPage(pages), (link) =>
                                  replaceItem(i, { ...item, items: [...item.items, link] }),
                                )
                              }
                            >
                              <Plus size={14} /> Add a link to it
                            </Button>
                          )}
                        </Row>
                      )
                    }
                    return (
                      <Row
                        key={i}
                        {...common}
                        name={linkName(item, pages)}
                        icon={item.icon}
                        detail={linkDetail(item, pages)}
                        onEdit={() => editLink('Edit the menu item', item, (link) => replaceItem(i, link))}
                      />
                    )
                  })}
                </ol>
              )}

              {editable && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" disabled={atTop} onClick={() => editLink('Add a page', blankPage(pages), (link) => setItems([...items, link]))}>
                    <Plus size={14} /> Page
                  </Button>
                  <Button variant="outline" size="sm" disabled={atTop} onClick={() => editLink('Add a link', blankLink(), (link) => setItems([...items, link]))}>
                    <Plus size={14} /> Link
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={atTop}
                    onClick={() => editLink('Add a section on a page', blankSection(pages), (link) => setItems([...items, link]))}
                  >
                    <Plus size={14} /> Section
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={atTop}
                    onClick={() =>
                      setEditing({
                        kind: 'dropdown',
                        title: 'Add a dropdown',
                        value: '',
                        icon: null,
                        save: (label, icon) => setItems([...items, withIcon<MenuItem>({ kind: 'dropdown', label, items: [] }, icon)]),
                      })
                    }
                  >
                    <Plus size={14} /> Dropdown
                  </Button>
                  {atTop && <span className="text-xs text-muted-foreground">The header holds {limits.top_items} — remove one to add another.</span>}
                  <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setHeader(null)}>
                    Back to automatic
                  </Button>
                </div>
              )}

              <div className="rounded-xl bg-muted p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">Button at the right</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {header.cta ? `“${linkName(header.cta, pages)}” → ${linkDetail(header.cta, pages)}` : 'None — your Login link is the only button.'}
                    </p>
                  </div>
                  {editable && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          editLink(
                            header.cta ? 'Edit the button' : 'Add a button',
                            header.cta ?? blankPage(pages),
                            (link) => setHeader({ items, cta: link }),
                            true,
                          )
                        }
                      >
                        {header.cta ? 'Edit' : 'Add a button'}
                      </Button>
                      {header.cta && (
                        <Button variant="ghost" size="sm" aria-label="Remove the button" onClick={() => setHeader({ items, cta: null })}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          <PortalControl
            surface="header"
            value={effectivePortal(headerPortal)}
            editable={editable}
            signupAllowed={signupAllowed}
            onChange={setHeaderPortal}
          />
        </CardBody>
      </Card>

      {/* ── 3. the footer ───────────────────────────────────────────────── */}
      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold text-foreground">Footer</h2>
              <p className="text-sm text-muted-foreground">
                {footer
                  ? 'Your own columns of links, and what else the footer shows.'
                  : 'Today’s footer: your name, your contact line, your menu, your client portal links and your social links.'}
              </p>
            </div>
            {footer && <Counter used={columns.length} max={limits.footer_columns} what="columns" />}
          </div>

          {!footer ? (
            editable && (
              <Button variant="outline" onClick={() => setFooter({ columns: [], blocks: { ...saved.footer_block_defaults } })}>
                Customise the footer
              </Button>
            )
          ) : (
            <>
              {columns.length > 0 && (
                <ol className="grid gap-3 md:grid-cols-2" aria-label="Footer columns">
                  {columns.map((column, i) => (
                    <li key={i} className="space-y-2 rounded-xl border border-border p-3">
                      <div className="flex items-end gap-2">
                        <Field label={`Column ${i + 1} title`} className="min-w-0 flex-1">
                          {(control) => (
                            <Input
                              {...control}
                              value={column.title ?? ''}
                              disabled={!editable}
                              placeholder="The firm"
                              onChange={(e) =>
                                setColumns(columns.map((c, j) => (j === i ? { ...c, title: e.target.value || null } : c)))
                              }
                            />
                          )}
                        </Field>
                        {editable && (
                          <>
                            <Button variant="ghost" size="sm" aria-label={`Move column ${i + 1} left`} disabled={i === 0} onClick={() => setColumns(moveItem(columns, i, i - 1))}>
                              <ArrowUp size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" aria-label={`Remove column ${i + 1}`} onClick={() => setColumns(columns.filter((_, j) => j !== i))}>
                              <Trash2 size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                      <ol className="space-y-2">
                        {column.links.map((link, j) => (
                          <Row
                            key={j}
                            editable={editable}
                            name={linkName(link, pages)}
                            detail={linkDetail(link, pages)}
                            first={j === 0}
                            last={j === column.links.length - 1}
                            onUp={() => setColumns(columns.map((c, k) => (k === i ? { ...c, links: moveItem(c.links, j, j - 1) } : c)))}
                            onDown={() => setColumns(columns.map((c, k) => (k === i ? { ...c, links: moveItem(c.links, j, j + 1) } : c)))}
                            onRemove={() => setColumns(columns.map((c, k) => (k === i ? { ...c, links: c.links.filter((_, m) => m !== j) } : c)))}
                            onEdit={() =>
                              editFooterLink('Edit the link', link, (next) =>
                                setColumns(columns.map((c, k) => (k === i ? { ...c, links: c.links.map((l, m) => (m === j ? next : l)) } : c))),
                              )
                            }
                          />
                        ))}
                      </ol>
                      <div className="flex items-center justify-between gap-2">
                        {editable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={column.links.length >= limits.footer_links}
                            onClick={() =>
                              editFooterLink('Add a link', blankPage(pages), (next) =>
                                setColumns(columns.map((c, k) => (k === i ? { ...c, links: [...c.links, next] } : c))),
                              )
                            }
                          >
                            <Plus size={14} /> Add a link
                          </Button>
                        )}
                        <Counter used={column.links.length} max={limits.footer_links} what="links" />
                      </div>
                    </li>
                  ))}
                </ol>
              )}
              {editable && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={columns.length >= limits.footer_columns}
                    onClick={() => setColumns([...columns, { title: null, links: [] }])}
                  >
                    <Plus size={14} /> Column
                  </Button>
                  <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setFooter(null)}>
                    Back to today’s footer
                  </Button>
                </div>
              )}

              {/* C24 item 14 — where each thing sits, and how loud. */}
              <FooterArrangement
                value={footer.arrangement ?? null}
                keys={saved.footer_arrangement_keys ?? []}
                sizes={saved.footer_sizes ?? []}
                maxSlot={saved.footer_max_slot ?? 4}
                editable={editable}
                onChange={(next) =>
                  setFooter({ ...footer, ...(next ? { arrangement: next } : { arrangement: null }) })
                }
              />

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-foreground">Also in the footer</legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  {/* `client_login` is the portal control's now (C18). */}
                  {saved.footer_blocks.filter((name) => name !== 'client_login').map((name) => (
                    <div key={name} className="rounded-lg border border-border p-3">
                      <Switch
                        checked={footer.blocks[name] ?? false}
                        disabled={!editable}
                        onChange={(e) => setFooter({ ...footer, blocks: { ...footer.blocks, [name]: e.target.checked } })}
                        label={FOOTER_BLOCK_LABELS[name]?.label ?? name}
                      />
                      {name === 'social' || name === 'map' ? (
                        <p className="mt-1 text-xs text-muted-foreground" data-footer-note={name}>
                          Set in{' '}
                          <Link to={SOCIAL_SETTINGS_HREF} className="font-medium text-primary underline">
                            Social profiles &amp; map
                          </Link>
                          {facts && !facts[name]
                            ? name === 'social'
                              ? ' — no profiles added yet, so this draws nothing.'
                              : ' — no map link added yet, so this draws nothing.'
                            : null}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-muted-foreground">{FOOTER_BLOCK_LABELS[name]?.note}</p>
                      )}
                    </div>
                  ))}
                </div>
              </fieldset>
            </>
          )}

          <PortalControl
            surface="footer"
            value={effectivePortal(footerPortal, footer ? footer.blocks.client_login !== false : true)}
            editable={editable}
            signupAllowed={signupAllowed}
            onChange={setFooterPortal}
          />

          <p className="text-xs text-muted-foreground">
            Always at the bottom, whatever you choose: your profession’s required disclaimers, the
            copyright line, and “Powered by FiveOX”.
          </p>

          {/* C24 item 13 (ADR-0046 §4): the same drawings the look page offers,
              showing what this firm has chosen — one component, two screens, so
              the two can never disagree about what `split` looks like. */}
          <ChosenLayouts header={saved.header_layout} footer={saved.footer_layout} />
        </CardBody>
      </Card>

      {/* ── save ────────────────────────────────────────────────────────── */}
      {problems.length > 0 && (
        <Alert tone="warning">
          <p className="font-medium">Fix these before saving:</p>
          <ul className="mt-1 list-disc pl-5" aria-label="What would be refused">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}
      <EditBar
        dirty={dirty}
        saving={busy}
        editable={editable}
        onSave={() => void save()}
        onDiscard={discard}
        problem={problems.length > 0 ? 'Fix what is listed above before saving.' : lineProblem}
        previewHref={previewHref}
      />

      {editing?.kind === 'link' && (
        <LinkDialog
          editing={editing}
          pages={pages}
          limits={limits}
          loadAnchors={loadAnchors}
          icons={icons}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === 'dropdown' && (
        <DropdownDialog editing={editing} max={limits.label} icons={icons} onClose={() => setEditing(null)} />
      )}
    </EditPage>
  )
}

/** The route: `/site/navigation`. */
export function SiteNavigation() {
  const { can } = useEvaluation()
  const editable = can(SITE, 'can_update')
  const { site: current, vocabulary } = useSite()
  const previewHref = useDraftPreviewHref()
  const [data, setData] = useState<Navigation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [signupAllowed, setSignupAllowed] = useState<boolean | null>(null)

  const load = useCallback(async () => {
    const body = await site.navigation()
    setData(body.data)
  }, [])

  useEffect(() => {
    void load().catch((err) =>
      setError(err instanceof ApiError ? err.message : 'Your navigation could not be loaded.'),
    )
  }, [load])

  // C18: whether client sign-up is on, so the Register switch can say why it
  // is off. A read that fails leaves it unknown and the switch enabled — the
  // site still hides Register while sign-up is off (I-6).
  useEffect(() => {
    let live = true
    signIn.read().then(
      (body) => live && setSignupAllowed(body.data.allow_signup),
      () => live && setSignupAllowed(null),
    )
    return () => {
      live = false
    }
  }, [])

  if (error) return <Alert tone="error">{error}</Alert>
  if (!data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  return (
    <NavigationEditor
      initial={data}
      editable={editable}
      previewHref={previewHref}
      facts={footerFacts(current)}
      signupAllowed={signupAllowed}
      icons={vocabulary?.icons ?? null}
      onSave={async (body) => (await site.saveNavigation(body)).data}
    />
  )
}
